import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

import appValidator from '@/lib/appValidator';
import dataControllerSubModel from "@/lib/dataControllerSubModel";

import radio from "@/models/submodels/radio";
import checkbox from "@/models/submodels/checkbox";
import picker from "@/models/submodels/picker";

import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import DialogContext from "@/ui/DialogContext/DialogContext";
import LoaderContext from "@/components/LoaderContext/LoaderContext";

import { DoCloseResponse, DoCloseResponseFail, DoCloseResponseSuccess, DoDeleteResponse, DoInsertResponse, DoInsertResponseFail, DoInsertResponseSuccess, DoUpdateResponse, DoUpdateResponseFail, DoUpdateResponseSuccess, FormControllerProps, PickerDataControllers, ServerErrorPromise } from "@/@types/components/formController";
import { FieldAny, IFieldPickerModel, IFieldsCollection } from "@/@types/models/model";
import { DCFieldValue, DCInsertResponseSuccess, DCRecord, DCResponseFail } from "@/@types/lib/dataController";
import { RecordValidation } from "@/@types/lib/appValidator";
import { ConfirmResult } from "@/@types/lib/confirmDialog";

//Types
import { SnackbarContextType } from "@/@types/ui/SnackbarContext";
import { LoaderContextType } from "@/@types/context/LoaderContext";

function useFormController(props: FormControllerProps) {

  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const dialogContext = useContext(DialogContext);
  const loaderContext = useContext(LoaderContext) as LoaderContextType;

  const [validation, setValidation] = useState<RecordValidation>({});
  const [validated, setValidated] = useState(false);
  const [subModels, setSubModels] = useState<PickerDataControllers>({});
  const [dataChanged, setDataChanged] = useState(false);
  const [fields, setFields] = useState<IFieldsCollection>([]);
  const [record, setRecord] = useState<DCRecord>({});

  const [lastChangedField, setLastChangedField] = useState<string>();
  const [lastChangeAt, setLastChangeAt] = useState<Date>();

  const [originalRecord, setOriginalRecord] = useState<DCRecord>({});

  const { t } = useTranslation();

  const { dc, form, mode, recordId, defaultValues, initialRecord, onDataUpdate, secondary, fieldNames, controllerForm, disablePickerItems } = props;

  const validator = new appValidator(t);

  let mounted = false;

  useEffect(() => {
    mounted = true;
    const secondaryPath = secondary && recordId !== undefined ? secondary.replace("__split__", recordId.toString()) : undefined;

    if (recordId !== undefined) {

      loaderContext.toggleLoading(true);
      dc.GetDataSingle(recordId, secondaryPath)
        .then(resp => {
          if (!resp.success) {
            // do some record handling
          }
          const rec = resp.data as DCRecord;
          if (defaultValues && mode === "insert") {
            Object.keys(defaultValues).forEach(key => {
              //@ts-ignore
              if (!record.hasOwnProperty(key) || rec[key] === undefined || rec[key] === null) {
                record[key] = defaultValues[key];
              }
            })
          }

          const recordCopy = Object.assign({}, rec, record);
          const originalRecord = Object.assign({}, rec);

          setRecord(recordCopy);
          setOriginalRecord(originalRecord);

        })
        .catch(err => {
          console.warn("Error fetching record from formController ", err);
        })
        .finally(() => {
          loaderContext.toggleLoading(false);
        });

    } else if (initialRecord !== undefined) {
      const recordCopy = Object.assign({}, initialRecord);
      const originalRecord = Object.assign({}, initialRecord);

      setRecord(recordCopy);
      setOriginalRecord(originalRecord);

    } else if (mode === "insert") {
      const rec = getDefaultValues();
      const recordCopy = Object.assign({}, rec);
      const originalRecord = Object.assign({}, rec);

      setRecord(recordCopy);
      setOriginalRecord(originalRecord);
    }

    const fs = getFields();
    setFields(fs);

    const fieldsWithModel = getFieldsWithModel(fs);

    Promise.all(fieldsWithModel).then(fModels => {
      const subModels = {};
      fModels.forEach(f => {
        const modelType = f.type === 'picker' ? picker
          : f.type === 'radio' ? radio
            : f.type === 'checkbox' ? checkbox
              : null;
        if (modelType !== null) {
          Object.assign(subModels, { [f.source]: new dataControllerSubModel(modelType, f, disablePickerItems && disablePickerItems[f.source] ? disablePickerItems[f.source] : []) })
        }
      })
      setSubModels(subModels);
    })
      .catch(err => {
        console.error("Error catching submodels", err);
      })

    return () => { mounted = false; }
  }, [recordId, initialRecord, mode, disablePickerItems]);

  useEffect(() => {
    if (validated && lastChangedField !== undefined) {
      validateField(lastChangedField);
    }
    setDataChanged(checkIfChanged(record));

  }, [lastChangeAt, lastChangedField])

  const getDefaultValues = () => {
    const rec = {};

    //set default values from fields
    dc.fields.forEach(f => {
      if (f.items && f.items.hasOwnProperty('default')) {
        if (f.type === 'boolean') {
          //@ts-ignore
          rec[f.source] = f.items.default;
        } else if (f.type === 'radio') {
          if (f.items.default !== undefined) {
            const pos = f.items.values.indexOf(f.items.default);
            //@ts-ignore
            rec[f.source] = {
              label: t(f.items.labels[pos]),
              value: f.items.default
            }
          }
        } else if (f.type === 'checkbox') {
          const valueArray = Array.isArray(f.items.default) ? f.items.default : [f.items.default];
          //@ts-ignore
          rec[f.source] = valueArray;
        }
      }
    })

    //set default values from props
    if (defaultValues !== undefined && defaultValues !== null) {
      Object.keys(defaultValues).forEach(key => {
        //@ts-ignore
        rec[key] = defaultValues[key];
      })
    }

    return rec;
  }

  const getFieldsWithModel = (fields: IFieldsCollection) => {
    const fieldModels = fields.filter(f => f.hasOwnProperty("subModel")).map(f => f as IFieldPickerModel);
    return fieldModels;
  }

  const getFields = () => {
    let formId = form ? form : mode;

    let fs: IFieldsCollection = [];

    //get all fields for controller
    if (controllerForm) {
      fs = dc.getFormFields(controllerForm);
    }

    //override with custom definitions
    let formFields = dc.getFormFields(formId);
    if (formFields.length > 0) {
      if (disablePickerItems) {
        Object.keys(disablePickerItems).forEach((pickerName) => {
          const field = formFields.find(f => f.type === 'picker' && f.source === pickerName);
          if (field?.items) {
            field.items.disabled = disablePickerItems[pickerName];
          }
        });
      }
      return formFields;
    }
    formFields.forEach(f => {
      const ind = fields.findIndex(x => x.source == f.source);
      if (ind >= 0) {
        fs[ind] = f;
      } else {
        fs.push(f);
      }
    })

    //filter by fieldnames if necessary
    if (fieldNames) {
      return fs.filter(x => fieldNames.indexOf(x.source) >= 0);
    } else {
      return fs;
    }
  }

  const checkIfChanged = (record: DCRecord) => {
    // console.log(record, originalRecord)
    const changed = validator.checkIfRecordChanged(record, originalRecord);
    return changed;
  }

  const validateField = (source: string) => {
    const field = fields.find(f => f.source === source);

    if (field !== undefined) {
      const fieldValidation = validator.validateField(record, field);

      setValidation((prevState) => ({
        ...prevState,
        [source]: fieldValidation
      }))
    }
  }

  const getValidator = () => {

    const formId = form ? form : mode;
    const validateFunc = dc.getValidator(formId);
    if (validateFunc !== null && validateFunc.toString() !== 't') {
      if (validator[validateFunc] !== undefined && typeof validator[validateFunc] === 'function') {
        return validator[validateFunc];
      } /*else {
        // return (record: any, t: any) => ({});
      }*/
    } /*else {
      // return (record: any, t: any) => ({});
    }*/
    return null;
  }

  const validate = () => {
    //model validation
    let validation = validator.validateModel(record, fields);

    //custom validation
    const validatorFn = getValidator();
    const customValidation = validatorFn !== null ? validatorFn(record, t) : {};

    const finalValidation = validator.mergeValidation(validation, customValidation);
    const isValid = validator.checkIfValid(finalValidation);

    setValidation(finalValidation);
    setValidated(true);

    return isValid;
  }

  const serverErrorPromise = (serverValidation: RecordValidation): Promise<ServerErrorPromise> => {
    setValidation(serverValidation);
    return Promise.resolve({
      success: false,
      validationPass: false,
      validation: serverValidation
    });
  }

  // *** Confirmations ***

  const confirmUpdate = () => {

    const checkFields = fields.filter(f => f.validation && f.validation.confirmChange);
    const changedFields = checkFields.filter(f => validator.checkIfFieldChanged(record[f.source], originalRecord[f.source]));

    const changePromises = changedFields.map(f => new Promise(resolve => {
      return confirmUpdateField(f)
        .then((resp) => { return Promise.resolve({ confirmed: true }) })
        .catch(err => { return Promise.resolve({ canceled: true }) })
    }))

    return Promise.all(changePromises).
      then(resp => {
        return Promise.resolve({ confirmed: true });
      })
      .catch(err => {
        return Promise.reject({ canceled: true });
      })
  }

  const confirmUpdateField = (field: FieldAny): Promise<ConfirmResult> => {

    const oldValue = originalRecord[field.source];
    const newValue = record[field.source];

    const title = "Želite li doista izmijeniti vrijednost polja '" + field.title + "'?";
    const text = "Stara vrijednost: " + oldValue + ", nova vrijednost: " + newValue;
    const confirmButtonText = "Da, izmijeni";
    const cancelButtonText = "Ne, odustani";

    return dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText })
  }

  const confirmDelete = (): Promise<ConfirmResult> => {
    const title = t("cdialogs.are_you_sure");
    const text = t("cdialogs.once_deleted")
    const confirmButtonText = t("cdialogs.yes_delete")
    const cancelButtonText = t("cdialogs.no_cancel")

    return dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText })
  }

  const confirmUpdateClose = (): Promise<ConfirmResult> => {
    const title = t("cdialogs.changes");
    const text = t("cdialogs.close_text");
    const confirmButtonText = t("cdialogs.continue");
    const cancelButtonText = t("cdialogs.close");

    return dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText })
  }

  const confirmInsertClose = (): Promise<ConfirmResult> => {
    const title = t("cdialogs.changes");
    const text = t("cdialogs.close_text");
    const confirmButtonText = t("cdialogs.continue");
    const cancelButtonText = t("cdialogs.close");

    return dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText })
  }

  const getOnlyChangedFieldsRecord = (): DCRecord => {
    const obj: DCRecord = {};
    Object.keys(record).forEach((key: string) => {
      if (record[key] !== originalRecord[key]) {
        obj[key] = record[key];
      }
    });
    return obj;
  }


  // *** Export functions ***

  const onFieldChange = (value: DCFieldValue, source: string) => {

    setRecord((prevState) => ({
      ...prevState,
      [source]: value
    }));

    setLastChangedField(source);
    setLastChangeAt(new Date());
  }

  const doInsert = (): Promise<DoInsertResponse> => {

    const isValid = validate();

    if (isValid) {
      loaderContext.toggleLoading(true);
      return dc.InsertRecord(record)
        .then((response) => {
          const id = (response as DCInsertResponseSuccess).id;
          if (onDataUpdate) {
            onDataUpdate();
          }
          setDataChanged(false);
          return Promise.resolve({ success: true, id: id } as DoInsertResponseSuccess);

        })
        .catch((response) => {
          switch ((response as DCResponseFail).errorCode) {
            case 401:
              return serverErrorPromise({
                email: {
                  valid: false,
                  msg:
                    t("register.emailInUse")
                }
              }) as Promise<DoInsertResponseFail>;
          }
          if (response.error?.detail?.code === 409) { // conflict, means we violated some unique constraint
            const fieldName = response.error?.detail?.field;
            const constraintType = response.error?.detail?.constraint;
            if (fieldName) {
              setValidation({
                [fieldName]: {
                  valid: false,
                  msg: t('error.' + constraintType + '.' + fieldName)
                }
              })
            } else {
              console.log("Constraint was violated during insert but no fieldname could be mapped to the constraint.")
            }
          }
          return Promise.resolve({
            success: false,
            validationPass: true,
            error: "Greška prilikom spremanja novih podataka!",
            data: response.error ?? null
          } as DoInsertResponseFail)
        })
        .finally(() => {
          loaderContext.toggleLoading(false);
        })
    } else {
      if (snackbarContext !== null) {
        snackbarContext.showNotification("messages.required_fields", "warning");
      }
      return Promise.resolve({ success: false, validationPass: false })
    }
  }

  const doUpdate = (): Promise<DoUpdateResponse> => {
    const id = recordId || record["id"] as number;
    if (id === undefined || id === null) {
      return Promise.reject({ success: false, error: "Unknown id" });
    }

    const isValid = validate();

    if (isValid) {
      loaderContext.toggleLoading(true);
      return confirmUpdate()
        .then((result: ConfirmResult) => {
          if (result.canceled) {
            return Promise.reject({ success: false, canceled: true });
          }
          else if (result.confirmed) {
            // const recordPart = getOnlyChangedFieldsRecord();
            const updRec = dc.httpUpdateVerb === "patch" ? getOnlyChangedFieldsRecord() : record;
            return dc.UpdateRecord(id, updRec, dc.httpUpdateVerb)
              .then((response) => {
                setDataChanged(false);
                setValidated(false);

                if (onDataUpdate) {
                  onDataUpdate();
                }

                return Promise.resolve({ success: true, updated: true } as DoUpdateResponseSuccess);

              })
              .catch((x) => {
                if (x.error?.detail?.code === 409) { // conflict, means we violated some unique constraint
                  const fieldName = x.error?.detail?.field;
                  const constraintType = x.error?.detail?.constraint;
                  if (fieldName) {
                    setValidation({
                      [fieldName]: {
                        valid: false,
                        msg: t('error.' + constraintType + '.' + fieldName)
                      }
                    })
                  } else {
                    console.log("Constraint was violated during update but no fieldname could be mapped to the constraint.")
                  }
                }
                return Promise.reject({ success: false, validationPass: true, error: "Greška prilikom ažuriranja podataka!", data: x.error ?? null } as DoUpdateResponseFail)
              })
              .finally(() => {
                loaderContext.toggleLoading(false);
              })
          } else {
            return Promise.reject({ success: false, error: "Unknown" } as DoUpdateResponseFail);
          }
        })

    } else {
      if (snackbarContext !== null) {
        snackbarContext.showNotification("messages.required_fields", "warning");
      }
      return Promise.resolve({ success: false, validationPass: false, validation: validation });
    }

  }

  const doDelete = (): Promise<DoDeleteResponse> => {
    const id = recordId || record["id"] as number;
    if (id === undefined || id === null) {
      return Promise.reject({ success: false, error: "Unknown id" });
    }

    return confirmDelete()
      .then(result => {
        if (result.canceled) {
          return Promise.resolve({ success: false, canceled: true });
        }
        if (result.confirmed) {
          return dc.DeleteRecord(id)
            .then(response => {
              if (onDataUpdate) {
                onDataUpdate();
              }
              return Promise.resolve({ success: true });
            })
            .catch(response => {
              return Promise.reject({ success: false, error: response.error ? response.error : "Greška prilikom brisanja podataka" });
            })

        }
        else {
          return Promise.reject({ success: false });
        }
      })

  }

  const doActivate = (activation: boolean): Promise<DoUpdateResponse> => {
    const id = recordId || record["id"] as number;
    if (id === undefined || id === null) {
      return Promise.reject({ success: false, error: "Unknown id" });
    }

    loaderContext.toggleLoading(true);

    return dc.ActivateRecord(activation, id)
      .then((response) => {
        if (response.success) {
          return Promise.resolve({ success: true, updated: true } as DoUpdateResponseSuccess);
        } else {
          return Promise.reject({ success: false, error: response.error } as DoUpdateResponseFail)
        }

      })
      .catch((x) => {
        return Promise.reject({ success: false, validationPass: true, error: "Greška prilikom aktiviranja!", data: x.error ?? null } as DoUpdateResponseFail)
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      })
  }

  const doCustomAction = (action: string, requestBody: DCRecord = {}) => {
    const id = recordId || record["id"] as number;
    if (id === undefined || id === null) {
      return Promise.reject({ success: false, error: "Unknown id" });
    }

    loaderContext.toggleLoading(true);


    // const recordPart = getOnlyChangedFieldsRecord();
    return dc.CustomActionOnRecord(id, action, requestBody)
      .then((response) => {
        if (response.success) {
          return Promise.resolve({ success: true, updated: true } as DoUpdateResponseSuccess);
        } else {
          return Promise.reject({ success: false, error: response.error } as DoUpdateResponseFail)
        }
      })
      .catch((x) => {
        return Promise.reject({ success: false, validationPass: true, error: "Greška prilikom aktiviranja!", data: x.error ?? null } as DoUpdateResponseFail)
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      })
  }

  const doClose = (externalDataChanged: boolean = false): Promise<DoCloseResponse> => {
    // console.log(dataChanged)
    if (dataChanged || externalDataChanged) {
      if (mode === "insert") {
        return confirmInsertClose()
          .then(result => {
            if (result.canceled || !result.confirmed) {
              return Promise.reject({ success: false, canceled: true } as DoCloseResponseFail);
            } else {
              return Promise.resolve({ success: true } as DoCloseResponseSuccess);
            }
          })
      } else if (mode === "update") {
        return confirmUpdateClose()
          .then(result => {
            if (result.canceled) {
              return Promise.reject({ success: false, canceled: true } as DoCloseResponseFail);
            }
            else if (!result.confirmed) {
              return Promise.reject({ success: false, shouldSave: true } as DoCloseResponseFail);
              //return doUpdate();
            } else {
              return Promise.resolve({ success: true } as DoCloseResponseSuccess);
            }
          });
      } else {
        return Promise.resolve({ success: true } as DoCloseResponseSuccess);
      }
    } else {
      return Promise.resolve({ success: true } as DoCloseResponseSuccess);
    }
  }

  const doClear = (revertDataChanged: boolean = false) => {
    setRecord({});
    if (revertDataChanged) {
      setDataChanged(false)
    }
    return Promise.resolve({ success: true });
  }

  const doPrepareSearch = () => {
    const isValid = validate();

    if (isValid) {
      return Promise.resolve({ success: true, filters: record })
    }
    else {
      if (snackbarContext !== null) {
        snackbarContext.showNotification("messages.required_fields", "warning");
      }
      return Promise.resolve({ success: false, validationPass: false });
    }
  }

  const doRefresh = () => {
    if (recordId) {

      loaderContext.toggleLoading(true);
      dc.GetDataSingle(recordId)
        .then(rec => {
          if (rec.success) {
            const recordCopy = Object.assign({}, rec.data) as DCRecord;
            const originalRecord = Object.assign({}, rec.data) as DCRecord;

            setRecord(recordCopy);
            setOriginalRecord(originalRecord);
          }

        })
        .catch(err => {
          console.warn("Error refreshing record from formController ", err);
        })
        .finally(() => {
          loaderContext.toggleLoading(false);
        });
    }
  }

  const doValidate = (): boolean => validate();


  return {
    validation,
    validated,
    subModels,
    dataChanged,
    fields,
    record,
    originalRecord,

    confirmFunctions: {
      confirmDelete,
      confirmInsertClose,
      confirmUpdate,
      confirmUpdateClose,
      confirmUpdateField
    },

    onFieldChange,
    doInsert,
    doUpdate,
    doDelete,
    doClose,
    doClear,
    doPrepareSearch,
    doRefresh,
    doValidate,
    doActivate,
    doCustomAction,
  }

}

export default useFormController;
